/**
 * QueryBuilder Store - weighting slice.
 *
 * Weighting-step mutators: apply/calculate mode, weights/dest fields, rim
 * dimensions (container + targets), filter, and normalization.
 */
import { StateCreator } from "zustand"
import { QueryBuilderStore, WeightingSlice } from "../types"
import { updateStep } from "../helpers"

export const createWeightingSlice: StateCreator<
  QueryBuilderStore,
  [["zustand/persist", unknown]],
  [],
  WeightingSlice
> = (set) => ({
  // ---- Weighting step mutators ----
  setWeightingMode: (stepId, mode) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting" ? { ...s, mode } : s
      )
    ),
  setWeightingField: (stepId, weightsField) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting" ? { ...s, weightsField } : s
      )
    ),
  setWeightingDest: (stepId, destField) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting" ? { ...s, destField } : s
      )
    ),
  addWeightingDimension: (stepId) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting"
          ? {
              ...s,
              dimensions: [
                ...s.dimensions,
                { container: "", targets: [] },
              ],
            }
          : s
      )
    ),
  removeWeightingDimension: (stepId, dimIndex) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting"
          ? {
              ...s,
              dimensions: s.dimensions.filter((_, i) => i !== dimIndex),
            }
          : s
      )
    ),
  setWeightingDimensionContainer: (stepId, dimIndex, container) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting"
          ? {
              ...s,
              dimensions: s.dimensions.map((d, i) =>
                i === dimIndex ? { ...d, container } : d
              ),
            }
          : s
      )
    ),
  // Bulk-replace one dimension's targets — used when its container is
  // dropped and the categories auto-populate from its BOOL children.
  setWeightingDimensionTargets: (stepId, dimIndex, targets) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting"
          ? {
              ...s,
              dimensions: s.dimensions.map((d, i) =>
                i === dimIndex ? { ...d, targets: [...targets] } : d
              ),
            }
          : s
      )
    ),
  setWeightingDimensionTargetValue: (stepId, dimIndex, targetIndex, target) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting"
          ? {
              ...s,
              dimensions: s.dimensions.map((d, i) =>
                i === dimIndex
                  ? {
                      ...d,
                      targets: d.targets.map((t, j) =>
                        j === targetIndex ? { ...t, target } : t
                      ),
                    }
                  : d
              ),
            }
          : s
      )
    ),
  setWeightingFilter: (stepId, filter) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting" ? { ...s, filter } : s
      )
    ),
  setWeightingNormalize: (stepId, normalize) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting" ? { ...s, normalize } : s
      )
    ),
  setWeightingNormalizeValue: (stepId, normalizeValue) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "weighting" ? { ...s, normalizeValue } : s
      )
    ),
})
